import React, { useState, useMemo, useEffect, useCallback } from 'react';
import { Card, CardContent } from './ui/card';
import { Button } from './ui/button';
import { Input } from './ui/input';
import { Badge } from './ui/badge';
import { CURRENCY } from '../lib/mockBank';
import {
  Lock, Unlock, Folder, Search, Banknote, Users, CheckSquare, Square, Loader2,
} from 'lucide-react';
import { toast } from 'sonner';

/**
 * BankAccessManager — teacher control panel for paid folder access.
 *
 * Operates on FOLDERS (a folder == a category path, mirroring the backend's unit of
 * access), never on individual bank files. Locking/opening a folder here writes the
 * REAL per-student `folder_access` the student app reads, so a folder the teacher
 * locks immediately shows as locked for the student too (question banks AND videos).
 *
 *   • Price per folder  → PUT /api/teacher/folder-settings  (scope topic|video)
 *   • Open / close       → POST /api/teacher/folders/access  (grant/revoke)
 *   • Current state      → GET  /api/teacher/folders/access  (per student)
 */

// Stable, DOM-safe id for a folder row (kind + category path).
const folderSlug = (kind, category) =>
  `${kind}-${(category || 'root').replace(/[^a-zA-Z0-9]+/g, '-').replace(/^-+|-+$/g, '')}` || `${kind}-root`;

const scopeOf = (kind) => (kind === 'video' ? 'video' : 'topic');

const BankAccessManager = ({ topics = [], students = [], videos = [], apiUrl, token }) => {
  const approved = useMemo(
    () => students.filter((s) => s.status === 'approved'),
    [students],
  );
  const studentName = (s) =>
    s?.full_name || s?.name || [s?.first_name, s?.family_name].filter(Boolean).join(' ') || s?.username || '—';

  const authHeaders = useMemo(
    () => ({ 'Content-Type': 'application/json', Authorization: `Bearer ${token}` }),
    [token],
  );

  // Real backend state -------------------------------------------------------
  const [priceMap, setPriceMap] = useState({});   // `${scope}::${category}` -> price
  const [accessMap, setAccessMap] = useState({});  // `${scope}::${category}::${studentId}` -> is_unlocked
  const [busy, setBusy] = useState(false);

  const fetchSettings = useCallback(async () => {
    if (!apiUrl) return;
    const next = {};
    for (const scope of ['topic', 'video']) {
      try {
        const r = await fetch(`${apiUrl}/api/teacher/folder-settings?scope=${scope}`, { headers: authHeaders });
        if (r.ok) {
          for (const s of await r.json()) next[`${scope}::${(s.category || '').trim()}`] = s.price || 0;
        }
      } catch { /* ignore */ }
    }
    setPriceMap(next);
  }, [apiUrl, authHeaders]);

  const fetchAccess = useCallback(async () => {
    if (!apiUrl) return;
    try {
      const r = await fetch(`${apiUrl}/api/teacher/folders/access`, { headers: authHeaders });
      if (r.ok) {
        const next = {};
        for (const a of await r.json()) {
          next[`${a.scope}::${(a.category || '').trim()}::${a.student_id}`] = !!a.is_unlocked;
        }
        setAccessMap(next);
      }
    } catch { /* ignore */ }
  }, [apiUrl, authHeaders]);

  useEffect(() => { fetchSettings(); fetchAccess(); }, [fetchSettings, fetchAccess]);

  // Both question-bank files and recorded lessons live inside folders (categories).
  const items = useMemo(() => [
    ...topics.map((t) => ({ ...t, _kind: 'topic' })),
    ...videos.map((v) => ({ ...v, _kind: 'video' })),
  ], [topics, videos]);

  // Group items into FOLDERS by (kind, category). Files with no category collapse
  // into the shared root folder, exactly as the student sees them.
  const folders = useMemo(() => {
    const map = new Map();
    for (const it of items) {
      const kind = it._kind;
      const category = (it.category || '').trim();
      const key = `${kind}::${category}`;
      if (!map.has(key)) {
        map.set(key, {
          key,
          slug: folderSlug(kind, category),
          kind,
          category,
          label: category ? category.split('/').filter(Boolean).pop() : 'المجلد الرئيسي',
          path: category,
          items: [],
        });
      }
      map.get(key).items.push(it);
    }
    return Array.from(map.values()).sort((a, b) =>
      a.kind === b.kind ? a.label.localeCompare(b.label, 'ar') : (a.kind === 'topic' ? -1 : 1));
  }, [items]);

  const foldersByKey = useMemo(
    () => Object.fromEntries(folders.map((f) => [f.key, f])),
    [folders],
  );

  const [selectedStudents, setSelectedStudents] = useState({});
  const [selectedFolders, setSelectedFolders] = useState({});
  const [studentSearch, setStudentSearch] = useState('');
  const [folderSearch, setFolderSearch] = useState('');

  const filteredStudents = useMemo(() => {
    const q = studentSearch.trim().toLowerCase();
    if (!q) return approved;
    return approved.filter((s) => studentName(s).toLowerCase().includes(q)
      || (s.username || '').toLowerCase().includes(q)
      || (s.grade || '').toLowerCase().includes(q));
  }, [approved, studentSearch]);

  const filteredFolders = useMemo(() => {
    const q = folderSearch.trim().toLowerCase();
    if (!q) return folders;
    return folders.filter((f) => f.label.toLowerCase().includes(q)
      || f.path.toLowerCase().includes(q)
      || f.items.some((it) => (it.title || '').toLowerCase().includes(q)));
  }, [folders, folderSearch]);

  const selectedStudentIds = Object.keys(selectedStudents).filter((id) => selectedStudents[id]);
  const selectedFolderKeys = Object.keys(selectedFolders).filter((k) => selectedFolders[k]);

  const allStudentsSelected = approved.length > 0 && selectedStudentIds.length === approved.length;
  const allFoldersSelected = folders.length > 0 && selectedFolderKeys.length === folders.length;

  const toggleStudent = (id) => setSelectedStudents((p) => ({ ...p, [id]: !p[id] }));
  const toggleFolder = (key) => setSelectedFolders((p) => ({ ...p, [key]: !p[key] }));

  const selectAllStudents = () => {
    if (allStudentsSelected) { setSelectedStudents({}); return; }
    const m = {}; approved.forEach((s) => { m[s.id] = true; }); setSelectedStudents(m);
  };
  const selectAllFolders = () => {
    if (allFoldersSelected) { setSelectedFolders({}); return; }
    const m = {}; folders.forEach((f) => { m[f.key] = true; }); setSelectedFolders(m);
  };

  const folderPrice = (f) => priceMap[`${scopeOf(f.kind)}::${f.category}`] ?? 0;

  const saveFolderPrice = async (f, raw) => {
    const value = Math.max(0, Number(raw) || 0);
    if (value === folderPrice(f)) return;
    try {
      const r = await fetch(`${apiUrl}/api/teacher/folder-settings`, {
        method: 'PUT', headers: authHeaders,
        body: JSON.stringify({ category: f.category, price: value, scope: scopeOf(f.kind) }),
      });
      if (r.ok) {
        setPriceMap((p) => ({ ...p, [`${scopeOf(f.kind)}::${f.category}`]: value }));
        toast.success(`سعر مجلد «${f.label}» = ${value} ${CURRENCY}`);
      } else toast.error('فشل حفظ السعر');
    } catch { toast.error('فشل الاتصال بالخادم'); }
  };

  const folderOpenForStudent = (f, sid) =>
    !!accessMap[`${scopeOf(f.kind)}::${f.category}::${sid}`];
  const folderOpenForAll = (f, ids) => ids.length > 0 && ids.every((sid) => folderOpenForStudent(f, sid));
  const folderOpenForSome = (f, ids) => ids.some((sid) => folderOpenForStudent(f, sid));

  const applyBulk = async (granted) => {
    if (selectedStudentIds.length === 0) return toast.error('اختر طالباً واحداً على الأقل');
    if (selectedFolderKeys.length === 0) return toast.error('اختر مجلداً واحداً على الأقل');
    setBusy(true);
    try {
      for (const key of selectedFolderKeys) {
        const f = foldersByKey[key];
        if (!f) continue;
        const r = await fetch(`${apiUrl}/api/teacher/folders/access`, {
          method: 'POST', headers: authHeaders,
          body: JSON.stringify({
            student_ids: selectedStudentIds,
            category: f.category,
            granted,
            scope: scopeOf(f.kind),
          }),
        });
        if (!r.ok) throw new Error('access update failed');
      }
      await fetchAccess();
      toast.success(
        granted
          ? `تم فتح ${selectedFolderKeys.length} مجلد لـ ${selectedStudentIds.length} طالب`
          : `تم إغلاق ${selectedFolderKeys.length} مجلد عن ${selectedStudentIds.length} طالب`,
      );
    } catch {
      toast.error('فشل تحديث صلاحيات الوصول');
    }
    setBusy(false);
  };

  return (
    <Card className="border-[#E8DFCF]">
      <CardContent className="p-5 space-y-5">
        {folders.length === 0 ? (
          <div className="text-center py-8 text-[#412402]/50 text-sm">
            لا توجد مجلدات بعد. أنشئ موضوعاً في بنك الأسئلة أو ارفع درساً أولاً.
          </div>
        ) : (
          <>
            <div className="grid md:grid-cols-2 gap-4">
              {/* Students column */}
              <div className="border border-[#E8DFCF] rounded-lg overflow-hidden">
                <div className="flex items-center justify-between bg-[#FAFAFA] px-3 py-2 border-b border-[#E8DFCF]">
                  <span className="text-sm font-semibold text-[#412402] flex items-center gap-1.5">
                    <Users className="w-4 h-4 text-[#EF9F27]" />
                    الطلاب ({selectedStudentIds.length}/{approved.length})
                  </span>
                  <button
                    onClick={selectAllStudents}
                    className="text-xs text-[#EF9F27] hover:text-[#D88E1F] flex items-center gap-1 font-medium"
                    data-testid="select-all-students"
                  >
                    {allStudentsSelected ? <CheckSquare className="w-3.5 h-3.5" /> : <Square className="w-3.5 h-3.5" />}
                    تحديد الكل
                  </button>
                </div>
                <div className="p-2">
                  <div className="relative mb-2">
                    <Search className="w-4 h-4 text-[#412402]/40 absolute right-2 top-1/2 -translate-y-1/2" />
                    <Input
                      placeholder="بحث عن طالب..."
                      value={studentSearch}
                      onChange={(e) => setStudentSearch(e.target.value)}
                      className="pr-8 h-8 text-sm"
                    />
                  </div>
                  <div className="max-h-56 overflow-y-auto space-y-0.5">
                    {filteredStudents.length === 0 ? (
                      <p className="text-center text-xs text-[#412402]/40 py-4">لا يوجد طلاب</p>
                    ) : filteredStudents.map((s) => (
                      <label
                        key={s.id}
                        className={`flex items-center gap-2 px-2 py-1.5 rounded-md cursor-pointer text-sm ${
                          selectedStudents[s.id] ? 'bg-amber-50' : 'hover:bg-[#FAFAFA]'
                        }`}
                        data-testid={`bank-student-${s.id}`}
                      >
                        <input
                          type="checkbox"
                          checked={!!selectedStudents[s.id]}
                          onChange={() => toggleStudent(s.id)}
                          className="accent-[#EF9F27]"
                        />
                        <span className="flex-1 min-w-0 truncate text-[#412402]">{studentName(s)}</span>
                        {s.grade && <span className="text-[10px] text-[#412402]/40 shrink-0">{s.grade}</span>}
                      </label>
                    ))}
                  </div>
                </div>
              </div>

              {/* Folders column */}
              <div className="border border-[#E8DFCF] rounded-lg overflow-hidden">
                <div className="flex items-center justify-between bg-[#FAFAFA] px-3 py-2 border-b border-[#E8DFCF]">
                  <span className="text-sm font-semibold text-[#412402] flex items-center gap-1.5">
                    <Folder className="w-4 h-4 text-[#EF9F27]" />
                    المجلدات ({selectedFolderKeys.length}/{folders.length})
                  </span>
                  <button
                    onClick={selectAllFolders}
                    className="text-xs text-[#EF9F27] hover:text-[#D88E1F] flex items-center gap-1 font-medium"
                    data-testid="select-all-folders"
                  >
                    {allFoldersSelected ? <CheckSquare className="w-3.5 h-3.5" /> : <Square className="w-3.5 h-3.5" />}
                    تحديد الكل
                  </button>
                </div>
                <div className="p-2">
                  <div className="relative mb-2">
                    <Search className="w-4 h-4 text-[#412402]/40 absolute right-2 top-1/2 -translate-y-1/2" />
                    <Input
                      placeholder="بحث عن مجلد..."
                      value={folderSearch}
                      onChange={(e) => setFolderSearch(e.target.value)}
                      className="pr-8 h-8 text-sm"
                    />
                  </div>
                  <div className="max-h-56 overflow-y-auto space-y-0.5">
                    {filteredFolders.map((f) => {
                      const unitLabel = f.kind === 'video' ? 'درس' : 'ملف';
                      return (
                        <div
                          key={f.key}
                          className={`flex items-center gap-2 px-2 py-1.5 rounded-md text-sm ${
                            selectedFolders[f.key] ? 'bg-amber-50' : 'hover:bg-[#FAFAFA]'
                          }`}
                          data-testid={`bank-folder-row-${f.slug}`}
                        >
                          <input
                            type="checkbox"
                            checked={!!selectedFolders[f.key]}
                            onChange={() => toggleFolder(f.key)}
                            className="accent-[#EF9F27]"
                          />
                          <Folder className="w-4 h-4 text-[#EF9F27] shrink-0" />
                          <div className="flex-1 min-w-0">
                            <p className="text-[#412402] truncate flex items-center gap-1">
                              <Badge className={`text-[9px] px-1 ${f.kind === 'video' ? 'bg-purple-100 text-purple-700' : 'bg-blue-100 text-blue-700'}`}>
                                {f.kind === 'video' ? 'فيديو' : 'أسئلة'}
                              </Badge>
                              {f.label}
                            </p>
                            <p className="text-[10px] text-[#412402]/40 truncate">
                              {f.path ? `${f.path} · ` : ''}{f.items.length} {unitLabel}
                            </p>
                          </div>
                          {/* Price editor (money) — applies to the whole folder */}
                          <div className="flex items-center gap-1 shrink-0">
                            <Banknote className="w-3.5 h-3.5 text-[#EF9F27]" />
                            <Input
                              // Remount when the fetched/saved price changes so the
                              // uncontrolled field shows the real value (not a stale 0).
                              key={`price-${f.key}-${folderPrice(f)}`}
                              type="number"
                              min={0}
                              step="0.5"
                              defaultValue={folderPrice(f)}
                              onClick={(e) => e.stopPropagation()}
                              onBlur={(e) => saveFolderPrice(f, e.target.value)}
                              className="h-7 w-16 text-xs text-center"
                              data-testid={`bank-price-${f.slug}`}
                              title="سعر المجلد بالمال"
                            />
                          </div>
                        </div>
                      );
                    })}
                  </div>
                </div>
              </div>
            </div>

            {/* Bulk action bar */}
            <div className="flex flex-wrap items-center gap-3 bg-[#FFF8E7] border border-[#F0E2C0] rounded-lg px-4 py-3">
              <span className="text-sm text-[#412402]/70">
                المحدد: <strong>{selectedStudentIds.length}</strong> طالب ·{' '}
                <strong>{selectedFolderKeys.length}</strong> مجلد
              </span>
              <div className="flex-1" />
              <Button
                onClick={() => applyBulk(true)}
                disabled={busy || !selectedStudentIds.length || !selectedFolderKeys.length}
                className="bg-[#EF9F27] hover:bg-[#D88E1F] text-white"
                data-testid="bulk-open-btn"
              >
                {busy ? <Loader2 className="w-4 h-4 ml-1 animate-spin" /> : <Unlock className="w-4 h-4 ml-1" />} فتح للمحددين
              </Button>
              <Button
                onClick={() => applyBulk(false)}
                disabled={busy || !selectedStudentIds.length || !selectedFolderKeys.length}
                variant="outline"
                className="border-red-200 text-red-600 hover:bg-red-50"
                data-testid="bulk-close-btn"
              >
                {busy ? <Loader2 className="w-4 h-4 ml-1 animate-spin" /> : <Lock className="w-4 h-4 ml-1" />} إغلاق عن المحددين
              </Button>
            </div>

            {/* Access overview for the currently selected students */}
            {selectedStudentIds.length > 0 && (
              <div>
                <h4 className="text-sm font-semibold text-[#412402] mb-2">
                  حالة الوصول {selectedStudentIds.length === 1 ? 'للطالب المحدد' : `للطلاب المحددين (${selectedStudentIds.length})`}
                </h4>
                <div className="flex flex-wrap gap-2">
                  {folders.map((f) => {
                    const openForAll = folderOpenForAll(f, selectedStudentIds);
                    const openForSome = folderOpenForSome(f, selectedStudentIds);
                    return (
                      <Badge
                        key={f.key}
                        className={`gap-1 ${
                          openForAll ? 'bg-green-100 text-green-800'
                            : openForSome ? 'bg-amber-100 text-amber-800'
                            : 'bg-[#412402]/10 text-[#412402]/60'
                        }`}
                      >
                        {openForAll ? <Unlock className="w-3 h-3" /> : <Lock className="w-3 h-3" />}
                        {f.label}
                      </Badge>
                    );
                  })}
                </div>
              </div>
            )}
          </>
        )}

        <p className="text-[11px] text-[#412402]/40">
          الفتح/الإغلاق يُطبَّق على المجلد كاملاً ويظهر فوراً عند الطالب. الإغلاق لا يلغي ما دفعه الطالب
          إلا بعد إعادة الفتح. السعر يُحدَّد لكل مجلد (وليس لكل ملف على حدة).
        </p>
      </CardContent>
    </Card>
  );
};

export default BankAccessManager;
